import type { IOption } from "@components/ui-kit";
import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  CreateProjectFromTemplateDocument,
  type CreateProjectFromTemplateMutation,
  type CreateProjectFromTemplateMutationVariables,
  type GetBrandContactsQuery,
  type GetProjectCreateDataQuery,
  GetProjectEditDataDocument,
  type GetProjectEditDataQuery,
  type GetProjectEditDataQueryVariables,
  ProjectTypeEnum,
  RateCardsDocument,
  type RateCardsQuery,
  type RateCardsQueryVariables,
} from "@src/__generated__/graphql";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { client } from "@src/services/apollo-client";
import { appStore } from "@src/stores/AppStore";
import { EXCHANGE_RATE_DENOMINATION } from "@src/stores/forms/SettingsModalState/FinancialSettingsState";
import { can } from "@src/utils/components/permissions";
import { denominate } from "@src/utils/formatters";
import mapToOptions from "@src/utils/map-to-options";
import { NumberModel } from "@src/utils/models";
import { getQueryParamOptional } from "@src/utils/routing";
import { FieldState } from "formstate";
import { compact, flatten, omit } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import Router from "next/router";
import React from "react";
import { FormProject } from "./FormProject";
import { BrandModel } from "./models";

export class StoreProjectForm {
  @observable clientSelectOpened = false;
  @observable.ref project: GetProjectEditDataQuery["project"] | undefined =
    undefined;
  @observable.ref form = new FormProject(this);
  @observable.ref projectManagerOptions: IOption[] = [];
  @observable.ref clientOptions: IOption[] = [];
  @observable.ref teamOptions: IOption[] = [];
  @observable.ref otherProjectManagerOptions: IOption[] = [];
  @observable projectUsesBudgetovac?: boolean;
  @observable projectIsFinished?: boolean;
  @observable canChangeProjectType = true;
  @observable canChangeFromTimeMaterialsToFixedPrice = true;
  @observable initialBillability?: boolean;
  @observable rateCardOptions: IOption[] = [];
  @observable rateCardsData: RateCardsQuery["projectRateCard"] = [];
  exchangeRate = new FieldState(
    NumberModel.fromValue(1, EXCHANGE_RATE_DENOMINATION),
  );

  lowestDateWithOpenProgress?: Date = undefined;
  lowestPossibleDateForStartDateChange?: Date = undefined;
  refetchProjectCreateData?: () => void = undefined;

  @observable brand?: BrandModel;
  @observable.ref contactOptions: IOption[] = [];

  templateFetcher = new FetchHelper<
    GetProjectEditDataQuery,
    GetProjectEditDataQueryVariables
  >(GetProjectEditDataDocument);

  private createFromTemplateMutator = new MutationHelper<
    CreateProjectFromTemplateMutation,
    CreateProjectFromTemplateMutationVariables
  >(CreateProjectFromTemplateDocument);

  constructor() {
    makeObservable(this);
    this.fetchRateCards();
  }

  @computed get isTemplate() {
    return getQueryParamOptional("isTemplate", Router.query) === "true";
  }

  @computed
  get fromTemplateId() {
    return getQueryParamOptional("fromTemplateId", Router.query);
  }

  @computed get isFromTemplate() {
    return Boolean(getQueryParamOptional("fromTemplateId", Router.query));
  }

  fromGetCreateProjectDataQuery(data: GetProjectCreateDataQuery) {
    this.assignFormOptions(data);
  }

  @action
  fromGetProjectEditDataQuery(data: GetProjectEditDataQuery) {
    this.project = data.project;
    this.form.brand_id.onChange(data.project?.brand.id!);
    this.fetchRateCards(data.project?.brand.id);
    this.form.note.onChange(data.project?.note!);
    this.form.title.onChange(data.project?.title!);
    this.form.code.onChange(data.project?.code!);
    this.form.start_date.reset(new Date(data.project?.start_date!));
    this.form.expected_end_date.reset(
      data.project?.expected_end_date
        ? new Date(data.project.expected_end_date)
        : undefined,
    );
    this.form.project_manager_id.onChange(data.project?.projectManager.id!);
    this.form.team_id.onChange(data.project?.team.id!);
    this.form.project_type.onChange(
      data.project?.type ?? ProjectTypeEnum.FixedPrice,
    );
    this.form.other_project_manager_ids.onChange(
      data.project?.otherProjectManagers?.map((pm) => pm.id) ?? [],
    );
    data.project?.categories.forEach(({ id }) => {
      this.form.category_ids.value?.push(id);
    });
    this.form.client_ids.onChange(data.project?.users.map((u) => u.id) ?? []);
    // TODO: find why without this line pm's wont get initialized
    this.form.other_project_manager_ids.$ =
      data.project?.otherProjectManagers?.map((pm) => pm.id) ?? [];
    this.form.contact_id.onChange(data.project?.contact?.id!);
    this.form.billable.onChange(data.project?.billable ?? false);
    this.form.rate_card.onChange(data.project?.rate_card_id ?? undefined);
    this.initialBillability = data.project?.billable ?? false;
    this.projectUsesBudgetovac = data.project?.uses_budgetovac;
    this.projectIsFinished = !!data.project?.finished_at!;
    this.lowestPossibleDateForStartDateChange = data.project
      ?.lowestPossibleDateForStartDateChange
      ? new Date(data.project.lowestPossibleDateForStartDateChange)
      : undefined;
    this.assignFormOptions(data);

    if (data.project?.type === ProjectTypeEnum.TimeMaterials) {
      const hasTasks =
        data.project?.ourWorkStructured?.some((work) =>
          work.budgetItems?.some((item) => item.tasks && item.tasks.length > 0),
        ) ?? false;

      if (hasTasks) {
        this.canChangeFromTimeMaterialsToFixedPrice = false;
      }
      return;
    }

    const budgetItems = data.project?.ourWorkStructured?.[0].budgetItems;

    if (!budgetItems || budgetItems.length === 1) return;
    this.canChangeProjectType = false;
  }

  @computed get showProBonoError() {
    return (
      !!this.project &&
      can("project_accessAccountingAttributes") &&
      !this.project.billable &&
      !!this.form.billable.dirty
    );
  }

  @computed get isFinancier() {
    return can("requestForInvoicing_accessAccountingAttributes");
  }

  @computed get billabilityChanged() {
    return this.initialBillability !== this.form.billable.$;
  }

  @action assignFormOptions(data: GetProjectEditDataQuery) {
    this.projectManagerOptions = mapToOptions.users(
      data.projectManagerSimpleMap,
    );

    this.lowestDateWithOpenProgress = data.lowestDateWithOpenProgress
      ? new Date(data.lowestDateWithOpenProgress)
      : undefined;

    this.clientOptions = flatten(
      data.clientsSimpleMap
        .map((client) => ({
          options:
            client.brands.map((brand) => ({
              label: brand.name,
              value: brand.id,
            })) ?? [],
        }))
        .map((client) => client.options),
    );

    this.teamOptions = data.teamSimpleMap.map((team) => ({
      label: team.name,
      value: team.id,
    }));
  }

  @computed
  get selectedRateCard() {
    return this.rateCardsData.find(
      (card) => card?.id === this.form.rate_card.$,
    );
  }

  @computed
  get showExchangeRateInput() {
    const currencyIso = this.selectedRateCard?.currency_iso;
    if (!currencyIso) return false;

    const workspaceSettings = appStore.workspaceStore.settings;
    if (currencyIso !== workspaceSettings?.currency.code) return true;

    return !!workspaceSettings?.currencyExchangeRatesStructured
      ?.find((rate) => rate.currency.code === currencyIso)
      ?.exchangeRates.some(
        (rate) =>
          Number(rate.exchange_rate) !==
          denominate(1, EXCHANGE_RATE_DENOMINATION),
      );
  }

  @action assignBrandContactOptions(data: GetBrandContactsQuery) {
    if (data.brand) this.brand = new BrandModel(data.brand);
    this.contactOptions = mapToOptions.contacts(data.brand?.contacts || []);
  }

  @action async fetchRateCards(brandId?: string) {
    try {
      const { data, error } = await client.query<
        RateCardsQuery,
        RateCardsQueryVariables
      >({
        query: RateCardsDocument,
        variables: {
          brandId: brandId,
        },
      });
      if (!data.projectRateCard || error) return;

      this.rateCardsData = data.projectRateCard;
      this.rateCardOptions = compact(
        data.projectRateCard.map((card) => {
          if (!card) return null;
          return {
            value: card.id,
            label: card.name,
          };
        }),
      );
    } catch (_) {}
  }

  @action.bound async fetchProjectTemplate(projectId: string) {
    const [data, error] = await this.templateFetcher.fetch({
      id: projectId,
    });

    if (error || !data.project) return;

    this.fromGetProjectEditDataQuery(data);
  }

  async onSelectClient(selected: Array<string>) {
    this.form.brand_id.onChange(selected[0]);
    this.clientSelectOpened = false;
  }

  static create() {
    return new StoreProjectForm();
  }

  async createProjectFromTemplate(): Promise<void> {
    if (!this.fromTemplateId) {
      appStore.UIStore.toast({
        status: "error",
        title: t`No template ID provided`,
      });
      captureEvent({
        message:
          "FE: No template ID provided while trying to create project from template",
      });
      return;
    }

    const input = omit(await this.form.validate(), "code", "is_template");

    if (input === null) return;

    if (!input.brand_id) {
      // NOTE: This should be just for typescript sake, since brand_id isn't required only for template creation
      appStore.UIStore.toast({
        status: "error",
        title: t`No brand ID provided`,
      });
      return;
    }

    const [data, error] = await this.createFromTemplateMutator.mutate({
      input: {
        ...input,
        brand_id: input.brand_id,
        template_id: this.fromTemplateId,
      },
    });

    if (error) return;

    Router.push({
      pathname: "/projects/detail",
      query: {
        id: data.createProjectFromTemplate.id,
      },
    });
  }

  @computed
  get isBrandSelectDisabled() {
    return (
      this.isTemplate &&
      this.form.brand_id.value ===
        appStore.workspaceStore.settings?.brandTemplateId
    );
  }
}

export const FormProjectCtx = React.createContext<StoreProjectForm>(
  null as any as StoreProjectForm,
);
